import type { SxProps, Theme } from '@mui/material';
import { CircularProgress, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import config from 'config';
import Logo from 'src/assets/newLogo.svg';
import type {
  AddItemToCartBody,
  GetCartResponse,
  GetStoreItemsSub,
  CartCheckoutBody,
} from 'src/openapi';
// Not the folder index: that also exports ProPitchSheet, which imports this.
import CareerProPitch from 'src/components/careerProPitch/CareerProPitch';
import { itemToCreditsTier } from 'src/components/creditsSlider';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { useNavigate } from 'react-router-dom';
import {
  logBuyDialogShown,
  type PaymentFunnelPopup,
  type PaymentFunnelTrigger,
} from 'src/helpers/paymentFunnel';
import { userSelf } from 'src/redux/actions/auth';
import { checkoutAbandoned } from 'src/redux/slices/assistant';
import { getApplicantStatusInfo } from 'src/redux/actions/candidateLayout';
import {
  addToCart,
  cartCheckout,
  getCartDetails,
  getMySubscription,
  getOrderHistory,
  getPaymentAddress,
  getStoreItems,
  paymentCancel,
  paymentFinish,
  updatePaymentAddress,
} from 'src/redux/actions/store';
import { getAddresses, updateStorePaymentAddress } from 'src/helpers/utils';
import useRazorpay from 'src/helpers/hooks/useRazorpay';
import {
  shownFreeCycles,
  isMandateCheckout,
  waitForMandateActivation,
} from 'src/helpers/subscription';
import { LS_KEY_STORE_POST_PURCHASE_RETURN_PATH } from 'src/constants/localStorageKeys';
import type { CheckoutDetails } from 'src/redux/types/store';
import PaymentNotSupported from 'src/pages/Store/components/common/PaymentNotSupported';
import AdminPaymentConfirm from 'src/pages/Store/components/common/AdminPaymentConfirm';
import PricingDetails from 'src/pages/Store/components/common/PricingDetails';
import { isProItemType } from 'src/components/careerProPitch/tiers';
import BillingAddressForm, {
  isBillingAddressValid,
} from './BillingAddressForm';
import BagsStep from './BagsStep';
import SummaryStep, {
  AddressShutter,
  CheckoutSplit,
  FadeIn,
  OrderSummaryHeading,
} from './SummaryStep';
import BillingAddressCard from './BillingAddressCard';
import { Dialog } from './styles/index.styles';
import type { BuyCreditsDialogContext } from './types';

// Any Pro tier on a mandate: the checkout the assistant follows up when it is
// left.
const isProMandateCart = (cart: GetCartResponse | null) =>
  !!cart?.cart?.some(
    (line) => isProItemType(line.item?.item_type) && !!line.item?.is_recurring,
  );

const perCredit = (b: GetStoreItemsSub) =>
  b.sku_size
    ? Number(b.gross_price_in_currency_after_disc ?? 0) / b.sku_size
    : Infinity;

type DialogView = 'gopro' | 'bags' | 'address' | 'summary';

// The confirmations this flow raises over itself. Above the modal layer, so
// they also clear a sheet that lifts itself over it -- the app-root AI-credit
// dialog (1400), which embeds this checkout -- instead of opening behind it.
const OVER_CHECKOUT_SX: SxProps<Theme> = {
  zIndex: (theme) => theme.zIndex.modal + 200,
};

// Shared out-of-credits paywall. Designed around intent, not inventory: one
// dominant Pro choice framed on outcome (get seen) + per-day price, with the
// cheap top-up tucked one tap away so it doesn't anchor Pro down.
const BuyCreditsDialog = ({
  open,
  onClose,
  creditsRequired,
  startOnCredits,
  context,
  directPurchase,
  embedded,
  aside,
  sx,
  trigger = 'out_of_credits',
  reason,
  funnelInstanceId,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * The Pro pitch showing this checkout came from (experimentFunnel.ts), sent
   * with the checkout so the order is tied back to its experiment arm. Set by
   * a caller that ran the pitch itself (ProPitchSheet); a pitch shown by this
   * dialog hands its own over on Go Pro.
   */
  funnelInstanceId?: string;
  /**
   * Render the flow inline instead of in its own Dialog.
   *
   * For a caller that is already a dialog and wants checkout as its next step
   * rather than a second sheet stacked on the first — the course paywall does
   * this. The caller owns the surface, the padding and the way back.
   */
  embedded?: boolean;
  /**
   * Embedded only: what the buyer is paying for, shown beside the summary.
   *
   * The course paywall passes the plan card the learner just chose, so the two
   * screens read as one — and so the step fills the wide sheet it inherits
   * instead of stacking everything into one tall column.
   */
  aside?: ReactNode;
  // Forwarded to the underlying Dialog — lets a caller nested inside another
  // high-z-index modal lift the paywall above it.
  sx?: SxProps<Theme>;
  // A known, specific shortfall (e.g. "you need 30 more credits for this").
  // Every entry point leads with the Pro pitch first regardless — this only
  // pre-selects the cheapest bag that covers the shortfall once the user
  // clicks through to the bags step. Omit for the generic paywall, where the
  // best-value bag is pre-selected instead.
  creditsRequired?: number;
  /**
   * Open straight on the credit packs instead of the Pro pitch.
   *
   * For callers whose own screen has already made the Pro case and is sending
   * the user here for the one thing Pro is not — credits. Everywhere else keeps
   * landing on the pitch first.
   */
  startOnCredits?: boolean;
  context?: BuyCreditsDialogContext;
  // Why it opened, for the admin payment funnel. Most callers open it on a
  // credit shortfall; the store and the course Pro paywall say otherwise.
  trigger?: PaymentFunnelTrigger;
  // What opened it, as a short slug ("course", "hidden_jobs"), for the funnel.
  reason?: string;
  /**
   * Skip the pitch and take one item straight to address + payment.
   *
   * This is how every store purchase now checks out — credits, rewards,
   * Spotlight, Pro — so there is a single place where a cart is priced, an
   * address is confirmed and a payment is taken, instead of a cart page and a
   * checkout page carrying second copies of all three.
   */
  directPurchase?: {
    itemId: string;
    quantity?: number;
    /** Omit to let the item's own pricing decide (gems-only items pay in gems). */
    mode?: 'Currency' | 'Gems';
  };
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Where to send a buyer back to after Pro bought from a pitch that asked for
  // it. Held here, not in localStorage like the course flow's: Razorpay pays
  // inside this page, so the success handler below still has it, and nothing is
  // left behind for a later purchase when a payment is abandoned some way that
  // never reaches a dismiss handler (a closed tab).
  const returnPathRef = useRef<string | null>(null);
  // The showing of this dialog's own pitch that led to Go Pro. Forgotten on
  // each opening: the dialog stays mounted, and a later opening is a new
  // showing (the server also ties it only to a Pro order).
  const pitchInstanceRef = useRef<string | null>(null);
  useEffect(() => {
    if (open) pitchInstanceRef.current = null;
  }, [open]);
  const store = useAppSelector((state) => state.store);
  const { user } = useAppSelector((state) => state.auth);
  const isRazorpayLoaded = useRazorpay(open);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<DialogView>('gopro');
  const [selectedBagId, setSelectedBagId] = useState<string | undefined>(
    undefined,
  );
  const [showPaymentNotSupported, setShowPaymentNotSupported] = useState(false);
  // True between opening a direct purchase and the cart/address resolving, so
  // the dialog shows a spinner rather than flashing the pitch it is skipping.
  const [preparing, setPreparing] = useState(false);
  // Editing the billing address from the summary drops a shutter open inside
  // this dialog rather than swapping to the full-page 'address' step — that
  // step stays for the case where there is no saved address to summarise yet.
  const [addressShutterOpen, setAddressShutterOpen] = useState(false);
  const [showAdminPaymentConfirm, setShowAdminPaymentConfirm] = useState(false);
  const [pendingCheckoutData, setPendingCheckoutData] =
    useState<CheckoutDetails | null>(null);
  // Carries the PricingDetails "use gems" checkbox state across the
  // admin-confirm detour, since handleConfirmAdminPayment runs later, in a
  // separate click.
  const [pendingUseGems, setPendingUseGems] = useState(false);
  // The cart snapshot taken right after adding the item — cartCheckout below
  // clears the cart, so anything read after that point (item types, net
  // value) must come from this, not from the live `store.cartDetails`. Kept
  // in state (not a ref) since AdminPaymentConfirm's amount reads it on render.
  const [cartSnapshot, setCartSnapshot] = useState<GetCartResponse | null>(
    null,
  );

  const methods = useForm<CheckoutDetails>({
    defaultValues: {
      name: '',
      email: '',
      phone: { number: '', country_code: '91' },
      shippingAddress: {
        address: '',
        pincode: '',
        state: '',
        country: 'India',
      },
      billingAddress: { address: '', pincode: '', state: '', country: 'India' },
      billingSameAsShipping: true,
    },
    mode: 'all',
  });
  const { control, reset, getValues, handleSubmit } = methods;
  // Set the moment Razorpay reports the autopay mandate approved, read by the
  // sheet's ondismiss so a post-success dismiss is not treated as an abandon.
  // A ref, not state: ondismiss can run in the same tick as the handler.
  const mandateApprovedRef = useRef(false);
  // One-shot guard for the direct-purchase effect below, so an item is carted
  // once per open rather than on every render `buy` is rebuilt on.
  const directStartedRef = useRef(false);
  // A Pro order summary is on screen: leaving it unpaid -- closing it, or
  // dismissing its payment sheet -- tells the assistant (checkoutAbandoned),
  // which may follow up in chat.
  const proCheckoutRef = useRef(false);
  const reasonRef = useRef(reason);
  // The page the summary was shown on: an unmount on navigation runs after
  // the route has already changed.
  const proPathRef = useRef('');
  const openRef = useRef(open);
  useEffect(() => {
    reasonRef.current = reason;
    openRef.current = open;
  }, [reason, open]);
  // Whoever closes it: this dialog, the sheet a caller embeds it in (the
  // course and AI-credit paywalls), or that caller unmounting it. Not the
  // close that hands over to the payment sheet -- that clears the flag first
  // and reports its own dismiss.
  const leaveUnpaid = useCallback(() => {
    if (!proCheckoutRef.current) return;
    proCheckoutRef.current = false;
    dispatch(checkoutAbandoned(reasonRef.current, proPathRef.current));
  }, [dispatch]);
  useEffect(() => {
    if (!open) leaveUnpaid();
  }, [open, leaveUnpaid]);
  useEffect(
    () => () => {
      openRef.current = false;
      leaveUnpaid();
    },
    [leaveUnpaid],
  );

  useEffect(() => {
    if (!store.shopItems.length) dispatch(getStoreItems());
    if (!store.cartDetails?.id) dispatch(getCartDetails());
  }, [dispatch]);

  // Re-sync each time the dialog opens — it's kept mounted between opens at
  // most call sites, so a fresh open must always land back on its entry view,
  // not whatever step/bag was left over from the last time it was open.
  useEffect(() => {
    if (open) {
      proCheckoutRef.current = false;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setView(startOnCredits ? 'bags' : 'gopro');
      setSelectedBagId(undefined);

      setPreparing(!!directPurchase?.itemId);
    }
  }, [open, startOnCredits, directPurchase?.itemId]);

  // Logged as it opens, with the screen it opens on; the bags log again if the
  // pitch's "Top up" brings them up.
  useEffect(() => {
    if (!open) return;
    let popup: PaymentFunnelPopup = 'pro_pitch';
    if (directPurchase?.itemId) popup = 'checkout';
    else if (startOnCredits) popup = 'credit_bags';
    logBuyDialogShown('credits', trigger, popup, reason);
  }, [open, trigger, reason]);

  const premium = store.shopItems.find(
    (i) => i.item_type === 'premium_profile_standard',
  );
  const bags = store.shopItems
    .filter((i) => i.item_type === 'credits')
    .sort((a, b) => (a.sku_size ?? 0) - (b.sku_size ?? 0));
  const bestBagId =
    bags.length > 1
      ? bags.reduce(
          (best, b) => (perCredit(b) < perCredit(best) ? b : best),
          bags[0],
        ).id
      : undefined;
  const loading = !store.shopItems.length;

  const bagTiers = useMemo(
    () =>
      bags
        .map(itemToCreditsTier)
        .filter((tier): tier is NonNullable<typeof tier> => !!tier),
    [bags],
  );
  // Cheapest pack that actually covers the known shortfall (bagTiers is
  // already sorted ascending) — falls back to the biggest pack if even that
  // doesn't cover it, rather than defaulting to a pack too small to help.
  const coveringBagId = creditsRequired
    ? (
        bagTiers.find(
          (tier) => tier.credits + tier.bonusCredits >= creditsRequired,
        ) ?? bagTiers[bagTiers.length - 1]
      )?.id
    : undefined;
  const effectiveBagId = bagTiers.some((tier) => tier.id === selectedBagId)
    ? selectedBagId
    : (coveringBagId ?? bestBagId ?? bagTiers[0]?.id);

  const isGemsOnly =
    (cartSnapshot?.net_value_in_currency || 0) === 0 &&
    (cartSnapshot?.net_value_in_gems || 0) > 0;

  // Reuses the exact address-save -> cartCheckout -> Razorpay-open sequence
  // Checkout.tsx's proceedToPayment runs, so a purchase made from anywhere
  // this dialog is opened goes through the same proven, tested payment path.
  const runPayment = async (
    data: CheckoutDetails,
    cartData: GetCartResponse | null,
    useGems: boolean,
  ) => {
    // A direct purchase never passes through the pitch's Go Pro, which is where
    // returnHere is otherwise recorded.
    if (context?.returnHere && !returnPathRef.current) {
      returnPathRef.current = window.location.pathname + window.location.search;
    }
    mandateApprovedRef.current = false;
    const proSheet = proCheckoutRef.current;
    // Pro's success page opens on hidden jobs, and their search is a full job
    // search: it runs while the buyer is in the payment sheet, so the rows are
    // there when they land. Loaded on demand, so checkout carries none of it.
    if (
      (cartData?.cart ?? []).some((line) => isProItemType(line.item?.item_type))
    ) {
      import('src/components/proShowcase/data')
        .then(({ prefetchHiddenJobs, loadShowcaseJob }) =>
          prefetchHiddenJobs(loadShowcaseJob().roleId),
        )
        // A head start only: the success page fetches them itself.
        .catch(() => undefined);
    }
    try {
      // Credits/top-ups only collect a billing address (per product
      // decision — shipping isn't needed for a digital purchase), but the
      // backend still expects both slots, so send the billing data as both.
      const billingOnly: CheckoutDetails = {
        ...data,
        shippingAddress: data.billingAddress,
        billingSameAsShipping: true,
      };
      const { shippingAddress, billingAddress } = getAddresses(billingOnly);

      const updateRes = await dispatch(
        updatePaymentAddress({ shippingAddress, billingAddress }),
      );
      if (!updatePaymentAddress.fulfilled.match(updateRes)) {
        setBusy(false);
        return;
      }

      const checkoutRes = await dispatch(
        cartCheckout({
          partial_payment: true,
          is_test: config.IS_TEST,
          use_gems: useGems,
          funnel_instance_id:
            funnelInstanceId ?? pitchInstanceRef.current ?? undefined,
          // The total on this screen -- the snapshot it shows, not the live
          // cart a countdown elsewhere may have re-read: never charged more.
          expected_net:
            (cartData ?? store.cartDetails)?.net_value_in_currency ?? null,
          // And the free months it showed: a "today only" free month that
          // ended is never charged today silently.
          expected_free_cycles: shownFreeCycles(cartData ?? store.cartDetails),
        } as CartCheckoutBody),
      );
      if (!cartCheckout.fulfilled.match(checkoutRes)) {
        const error = (checkoutRes.payload as { error?: string } | undefined)
          ?.error;
        if (error === 'pro_offer_ended' || error === 'price_changed') {
          // A time-limited price ended while they filled this in: the cart is
          // re-read (the server reprices it) and the new total is on screen
          // before they can pay it.
          const fresh = await dispatch(getCartDetails());
          if (getCartDetails.fulfilled.match(fresh)) {
            setCartSnapshot(fresh.payload as GetCartResponse);
          }
          dispatch(getStoreItems());
        }
        setBusy(false);
        return;
      }
      const { payload } = checkoutRes;

      const isSpotlightPurchase = (cartData?.cart ?? []).some(
        (cartItem) => cartItem.item?.item_type === 'applicant_spotlight',
      );
      const isPremiumPurchase = (cartData?.cart ?? []).some((cartItem) =>
        isProItemType(cartItem.item?.item_type),
      );
      const isCartGemsOnly =
        (cartData?.net_value_in_currency || 0) === 0 &&
        (cartData?.net_value_in_gems || 0) > 0;

      const runPostPaymentSuccess = async () => {
        // Paid: whatever closes or unmounts this now is no abandon.
        proCheckoutRef.current = false;
        await dispatch(userSelf());
        dispatch(getApplicantStatusInfo());
        await dispatch(getCartDetails());
        await dispatch(getOrderHistory());
        // Read before onClose: a caller that wrote the return path clears it in
        // its own close handler (an abandoned checkout must not leave one
        // behind), and that handler runs inside onClose.
        const returnPath =
          returnPathRef.current ??
          localStorage.getItem(LS_KEY_STORE_POST_PURCHASE_RETURN_PATH);
        returnPathRef.current = null;
        localStorage.removeItem(LS_KEY_STORE_POST_PURCHASE_RETURN_PATH);
        setBusy(false);
        onClose();
        if (isSpotlightPurchase && payload?.transaction_id) {
          navigate(`/store/spotlight-success/${payload.transaction_id}`);
          return;
        }
        // A mandate is worth confirming once, so Pro still lands on its success
        // page — but it is handed the return path so it can offer the way back.
        // Without this, a learner who bought the pass to open a locked lesson
        // was walked off to the store with no thread back to it. The need it
        // was bought for picks that page's main action.
        if (isPremiumPurchase) {
          const params = new URLSearchParams();
          if (returnPath) params.set('return', returnPath);
          if (context?.lead) params.set('need', context.lead);
          const query = params.toString();
          navigate(`/store/premium-success${query ? `?${query}` : ''}`);
          return;
        }
        if (returnPath) {
          const url = new URL(returnPath, window.location.origin);
          url.searchParams.set('checkoutSuccess', 'true');
          navigate(url.pathname + url.search);
          return;
        }
        // Credits and rewards have no success page of their own, and the
        // checkout page that used to land them on their order is gone — so a
        // purchase started from the store finishes on the order it created.
        // Started anywhere else (a course paywall, say) the buyer is
        // mid-task: closing the dialog returns them to what they were doing,
        // and yanking them to order history would lose their place.
        if (window.location.pathname.startsWith('/store')) {
          navigate('/store/orders-history');
        }
      };

      if (isCartGemsOnly || payload?.is_complete) {
        if (!payload?.transaction_id) {
          setBusy(false);
          return;
        }
        const finishRes = await dispatch(
          paymentFinish({ transaction_id: payload.transaction_id }),
        );
        if (paymentFinish.fulfilled.match(finishRes)) {
          await runPostPaymentSuccess();
        } else {
          setBusy(false);
        }
        return;
      }

      const options: any = {
        key: config.RAZORPAY_KEY,
        name: 'WBTS Pvt. Ltd.',
        description: 'Abekus Store Products',
        image: Logo,
        prefill: {
          name: data.name,
          email: data.email,
          contact: data.phone,
        },
        theme: { color: 'primary.main' },
        modal: {
          async ondismiss() {
            // Razorpay can fire ondismiss after a successful mandate
            // authorisation; reporting that as an abandon would cancel a
            // purchase the buyer just completed.
            if (mandateApprovedRef.current) {
              return;
            }
            await dispatch(
              paymentCancel({ transaction_id: payload?.transaction_id }),
            );
            await dispatch(getCartDetails());
            await dispatch(getOrderHistory());
            dispatch(userSelf());
            localStorage.removeItem(LS_KEY_STORE_POST_PURCHASE_RETURN_PATH);
            setBusy(false);
            // After the cancel: it puts the cart back, which the follow-up
            // reads the buyer's price from.
            if (proSheet)
              dispatch(checkoutAbandoned(reason, proPathRef.current));
          },
        },
      };

      if (isMandateCheckout(payload)) {
        options.subscription_id = payload.subscription_id;
        // Same contract as the store checkout page: a mandate carries no order
        // signature to verify, so the backend is told by Razorpay's
        // `subscription.authenticated` webhook. Poll for it, then run the same
        // post-success routine — this handler used to be empty, which left the
        // buyer staring at an open dialog after a successful purchase.
        options.handler = async () => {
          mandateApprovedRef.current = true;
          const { active } = await waitForMandateActivation(
            payload?.subscription_id,
          );
          await dispatch(getMySubscription());
          if (!active) {
            enqueueSnackbar(
              'Your subscription is being set up. Your benefits will appear here in a moment.',
              { variant: 'info', autoHideDuration: 8000 },
            );
          }
          await runPostPaymentSuccess();
        };
      } else if (payload?.razorpay_order_id) {
        options.order_id = payload.razorpay_order_id;
        options.handler = async (response: any) => {
          const finishRes = await dispatch(
            paymentFinish({
              transaction_id: payload.transaction_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          );
          if (paymentFinish.fulfilled.match(finishRes)) {
            await runPostPaymentSuccess();
          }
        };
        // payable_amount_in_currency reflects any gems discount applied
        // server-side — net_value_in_currency alone would overstate it.
        options.amount =
          payload?.payable_amount_in_currency ??
          cartData?.net_value_in_currency;
        options.currency = 'INR';
      }

      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', (resp: any) => {
        // A failed attempt is NOT a cancellation — the buyer routinely
        // retries with another method on the SAME order and succeeds, so
        // only record the attempt; terminal handling happens in ondismiss
        // (buyer closed the modal) or the success handler (retry worked).
        if (payload?.transaction_id) {
          const e = resp?.error ?? {};
          dispatch(
            paymentCancel({
              transaction_id: payload.transaction_id,
              failure: {
                razorpay_payment_id: e.metadata?.payment_id,
                razorpay_order_id: e.metadata?.order_id,
                error_code: e.code,
                error_reason: e.reason,
                error_source: e.source,
                error_step: e.step,
                error_description: e.description,
              },
            }),
          );
        }
      });
      proCheckoutRef.current = false;
      onClose();
      rzp.open();
    } catch {
      setBusy(false);
    }
  };

  // Fetches the saved address and validates it directly (not via RHF's
  // trigger/isValid — BillingAddressForm's Controllers haven't mounted yet
  // at this point, so those fields are unregistered and trigger() would
  // trivially resolve valid regardless of the actual saved address): valid
  // drops straight into the cart-total confirmation step, invalid/missing
  // drops into the inline billing form first.
  // Razorpay's script is deliberately NOT waited on here. It loads async, so on
  // the first purchase after a page load it is usually still in flight —
  // bailing out on that left the buyer on a spinner that never resolved, and
  // only the second attempt (script cached) ever worked. The pay button owns
  // that gate instead: it stays disabled until the script is ready.
  const proceedWithAddress = async (cartData: GetCartResponse | null) => {
    setCartSnapshot(cartData);
    // Only while still open: the buyer may close during the cart's spinner.
    proCheckoutRef.current = openRef.current && isProMandateCart(cartData);
    proPathRef.current = window.location.pathname + window.location.search;

    if ((window as any)?.ReactNativeWebView) {
      setShowPaymentNotSupported(true);
      setBusy(false);
      setPreparing(false);
      return;
    }
    const addrRes = await dispatch(getPaymentAddress());
    const savedAddress =
      getPaymentAddress.fulfilled.match(addrRes) && addrRes.payload
        ? updateStorePaymentAddress(addrRes.payload)
        : null;
    if (savedAddress) {
      reset(savedAddress);
    }
    setBusy(false);
    setPreparing(false);
    setView(
      savedAddress && isBillingAddressValid(savedAddress)
        ? 'summary'
        : 'address',
    );
  };

  const buy = async (
    itemId?: string,
    quantity = 1,
    mode?: 'Currency' | 'Gems',
  ) => {
    if (!itemId || busy) {
      setPreparing(false);
      return;
    }
    setBusy(true);
    // Fall back to the item's own pricing: a rewards item with no rupee price
    // is bought with gems, and forcing Currency on it would price the cart at
    // zero and take a payment for nothing.
    const item = [...store.shopItems, ...store.rewardItems].find(
      (i) => i.id === itemId,
    );
    const paymentMode =
      mode ??
      (item && !item.gross_price_in_currency_after_disc ? 'Gems' : 'Currency');
    // Only the item the user just picked — a purchase started from this
    // dialog should never carry over whatever was already in the cart.
    const items: AddItemToCartBody[] = [
      { item_id: itemId, quantity, item_payment_mode: paymentMode },
    ];
    const addRes = await dispatch(addToCart({ items, couponCode: null }));
    if (!addToCart.fulfilled.match(addRes)) {
      setBusy(false);
      // Whatever went wrong, the spinner must not outlive the attempt.
      setPreparing(false);
      return;
    }
    // addToCart already re-fetches the cart internally, but that updates
    // redux, not this closure — fetch again to read the fresh value directly.
    const cartRes = await dispatch(getCartDetails());
    const cartData = getCartDetails.fulfilled.match(cartRes)
      ? cartRes.payload
      : null;
    await proceedWithAddress(cartData ?? null);
  };

  // A direct purchase carts the item as soon as the dialog opens, so the buyer
  // lands on address/summary rather than on a pitch they have already read.
  useEffect(() => {
    if (!open) {
      directStartedRef.current = false;
      return;
    }
    if (directPurchase?.itemId && !directStartedRef.current) {
      directStartedRef.current = true;
      buy(directPurchase.itemId, directPurchase.quantity, directPurchase.mode);
    }
    // `buy` is rebuilt every render; the ref above is what keeps this to one
    // run per open, so it deliberately stays out of the dependency list.
  }, [
    open,
    directPurchase?.itemId,
    directPurchase?.quantity,
    directPurchase?.mode,
  ]);

  // The billing form's own "Continue" — purely local validation (registered
  // fields only), so it just advances to the cart-total confirmation step.
  const handleAddressSubmit = handleSubmit(() => {
    setView('summary');
  });

  // Same validation, but from the summary's inline shutter: on success the
  // shutter simply closes and the updated address shows on the card above it.
  const handleInlineAddressSubmit = handleSubmit(() => {
    setAddressShutterOpen(false);
  });

  const handleContinueToPayment = async (useGems: boolean) => {
    setBusy(true);
    const data = getValues();
    const finalCurrencyPayment = cartSnapshot?.net_value_in_currency || 0;
    // Admins confirm before a live charge — gems-only carts move no real
    // money, so they skip straight through.
    if (user?.is_admin && finalCurrencyPayment > 0) {
      setPendingCheckoutData(data);
      setPendingUseGems(useGems);
      setShowAdminPaymentConfirm(true);
      setBusy(false);
      return;
    }
    await runPayment(data, cartSnapshot, useGems);
  };

  const handleConfirmAdminPayment = async () => {
    setShowAdminPaymentConfirm(false);
    if (!pendingCheckoutData) return;
    const data = pendingCheckoutData;
    setPendingCheckoutData(null);
    setBusy(true);
    await runPayment(data, cartSnapshot, pendingUseGems);
  };

  const { name: billedName, billingAddress } = getValues();

  // What this paywall interrupted: the pitch shows it first, above its headline.
  const contextNote = context?.title ? (
    <span>
      <em>
        {context.title}
        {/[.!?]$/.test(context.title) ? '' : '.'}
      </em>
      {context.subtitle ? ` ${context.subtitle}` : ''}
    </span>
  ) : undefined;

  const content = (
    <>
      {preparing ? (
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'center', py: '64px' }}
        >
          <CircularProgress size={26} />
        </Stack>
      ) : view === 'address' ? (
        <BillingAddressForm
          control={control}
          onClose={onClose}
          onSubmit={handleAddressSubmit}
        />
      ) : view === 'summary' ? (
        <SummaryStep
          store={store}
          name={billedName}
          billingAddress={billingAddress}
          isGemsOnly={isGemsOnly}
          disabled={busy || (!isGemsOnly && !isRazorpayLoaded)}
          userGems={user?.gems}
          onChangeAddress={() => setAddressShutterOpen((prev) => !prev)}
          onContinue={handleContinueToPayment}
          addressOpen={addressShutterOpen}
          addressControl={control}
          onAddressSubmit={handleInlineAddressSubmit}
        />
      ) : view === 'bags' ? (
        <BagsStep
          loading={loading}
          bagTiers={bagTiers}
          effectiveBagId={effectiveBagId}
          busy={busy}
          hasPremium={!!premium}
          onSelect={setSelectedBagId}
          onAddToCart={() => buy(effectiveBagId)}
          onClose={onClose}
          onBackToGoPro={() => setView('gopro')}
        />
      ) : (
        <CareerProPitch
          placement="go_pro_dialog"
          loading={loading}
          busy={busy}
          premium={premium}
          proUnlocks={context?.proUnlocks}
          note={contextNote}
          ctaLabel={context?.ctaLabel}
          lead={context?.lead}
          courseName={context?.courseName}
          links={[
            // Cheap escape -- present but deliberately quiet so it doesn't
            // anchor Pro down.
            // One quiet way out: an opener's own alternative (e.g. "Use 324
            // credits instead") replaces the generic top-up.
            ...(bags.length > 0 &&
            !context?.hideTopUp &&
            !context?.secondaryAction
              ? [
                  {
                    label: 'Just need a few credits? Top up →',
                    onClick: () => {
                      setView('bags');
                      logBuyDialogShown(
                        'credits',
                        trigger,
                        'credit_bags',
                        reason,
                      );
                    },
                    cta: 'pro_pitch_top_up',
                  },
                ]
              : []),
            ...(context?.secondaryAction
              ? [{ ...context.secondaryAction, cta: 'pro_pitch_alternative' }]
              : []),
          ]}
          onClose={onClose}
          onGoPro={(instanceId, itemId) => {
            pitchInstanceRef.current = instanceId;
            if (context?.returnHere) {
              returnPathRef.current =
                window.location.pathname + window.location.search;
            }
            buy(itemId ?? premium?.id);
          }}
        />
      )}
    </>
  );

  // Two columns, assembled here rather than inside a step: `aside` keeps the
  // same slot while the right-hand side goes from spinner to summary, so the
  // card the caller is animating into place is never remounted mid-move.
  const onToggleAddress = () => setAddressShutterOpen((prev) => !prev);
  const summaryReady = !preparing && view === 'summary';
  const splitContent = (
    <CheckoutSplit>
      <Stack sx={{ gap: '12px' }}>
        {summaryReady && (
          <FadeIn>
            <OrderSummaryHeading cart={store.cartDetails} />
          </FadeIn>
        )}
        {summaryReady && (
          <FadeIn>
            <BillingAddressCard
              name={billedName}
              billingAddress={billingAddress}
              onChange={onToggleAddress}
              open={addressShutterOpen}
            />
          </FadeIn>
        )}
        {aside}
      </Stack>
      {preparing ? (
        <Stack sx={{ alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={26} />
        </Stack>
      ) : (
        <FadeIn>
          <PricingDetails
            store={store}
            variant="checkout"
            isGemsOnly={isGemsOnly}
            disabled={busy || (!isGemsOnly && !isRazorpayLoaded)}
            onContinue={handleContinueToPayment}
            userGems={user?.gems}
            fullWidth
            hideHeader
          />
        </FadeIn>
      )}
      {/* Over both columns, where the form has the width to lay its fields out
          two abreast — the one way it fits without a scrollbar of its own. */}
      {addressShutterOpen && (
        <AddressShutter>
          <BillingAddressForm
            control={control}
            onClose={onToggleAddress}
            onSubmit={handleInlineAddressSubmit}
            twoColumn
          />
        </AddressShutter>
      )}
    </CheckoutSplit>
  );

  return (
    <>
      {embedded ? (
        aside ? (
          splitContent
        ) : (
          content
        )
      ) : (
        <Dialog
          open={open}
          onClose={onClose}
          noPadding={view === 'bags'}
          pitch={view === 'gopro' && !preparing}
          sx={sx}
        >
          {content}
        </Dialog>
      )}
      <PaymentNotSupported
        sx={OVER_CHECKOUT_SX}
        open={showPaymentNotSupported}
        onClose={() => setShowPaymentNotSupported(false)}
      />
      <AdminPaymentConfirm
        sx={OVER_CHECKOUT_SX}
        open={showAdminPaymentConfirm}
        amount={cartSnapshot?.net_value_in_currency || 0}
        onClose={() => {
          setShowAdminPaymentConfirm(false);
          setPendingCheckoutData(null);
          setBusy(false);
        }}
        onConfirm={handleConfirmAdminPayment}
      />
    </>
  );
};

export default BuyCreditsDialog;
